/**
 * Tenant Registration Page - Multi-step registration flow for new tenants
 * Enhanced UI with "Dark Professional" Aesthetics, Fade Effects, and Bug Fixes
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Divider,
  Link,
  Chip,
  Fade,
  useTheme
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  CreditCard as CreditCardIcon,
  CheckCircle as CheckIcon,
  ArrowBack as ArrowBackIcon,
  Star as StarIcon,
  Check as CheckMarkIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';

import subscriptionService from '../../services/subscriptionService';
import tenantService from '../../services/tenantService';
import { storeUserData } from '../../services/authService';
import { formatCurrency } from '../../utils/currencyFormatter';
import axiosInstance from '../../utils/axios';
import API_CONFIG from '../../config/api.config';

// Dark Professional Color Palette
const COLORS = {
  primary: '#1a237e', // Deep Navy Blue
  secondary: '#37474f', // Blue Grey
  accent: '#0d47a1', // Bright Blue Accent
  background: '#eceff1', // Light Blue-Grey Background
  cardBg: '#ffffff',
  text: '#102027',
  muted: '#546e7a',
  success: '#1b5e20', // Dark Green
  warning: '#e65100',
  border: '#90a4ae' // Darker Blue Grey for better visibility
};

const FONT_FAMILY = '"Calibri", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif';

const formatPrice = (price, currencyCode = 'USD') => {
  return formatCurrency(price, { currencyCode });
};

// Plan feature flags shown on registration cards
const PLAN_FEATURE_FLAGS = [
  { id: 'loyalty_points', label: 'loyalty points' },
  { id: 'whatsapp_bill_share', label: 'whatsapp bill share' },
  { id: 'ecommerce_website', label: 'ecommerce website' },
  { id: 'webapp', label: 'web app' },
  { id: 'android_app', label: 'android app' },
];

const steps = [
  { label: 'Company Info', icon: <BusinessIcon /> },
  { label: 'Admin Account', icon: <PersonIcon /> },
  { label: 'Select Plan', icon: <CreditCardIcon /> },
  { label: 'Complete', icon: <CheckIcon /> }
];

/**
 * Common Styles
 */
const commonStyles = {
  root: {
    fontFamily: FONT_FAMILY,
    minHeight: '100vh',
    backgroundColor: COLORS.background,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    py: 4
  },
  paper: {
    p: { xs: 3, md: 5 },
    borderRadius: 2,
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)', // Softer shadow
    width: '100%',
    maxWidth: 1000,
    fontFamily: FONT_FAMILY,
    backgroundColor: COLORS.cardBg
  },
  title: {
    fontFamily: FONT_FAMILY,
    fontWeight: 700,
    color: COLORS.primary,
    textAlign: 'center',
    mb: 1,
    letterSpacing: '0.5px'
  },
  subtitle: {
    fontFamily: FONT_FAMILY,
    color: COLORS.muted,
    textAlign: 'center',
    mb: 4,
    fontSize: '1.1rem'
  },
  button: {
    fontFamily: FONT_FAMILY,
    fontWeight: 600,
    textTransform: 'none',
    px: 4,
    py: 1.2,
    borderRadius: 1,
    backgroundColor: COLORS.primary,
    '&:hover': {
      backgroundColor: COLORS.accent
    }
  },
  backButton: {
    fontFamily: FONT_FAMILY,
    color: COLORS.secondary,
    textTransform: 'none',
    fontWeight: 600
  },
  // Custom styled TextField styles
  textField: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fff',
      '& fieldset': {
        borderColor: COLORS.primary, // Force Navy Blue always
        borderWidth: '1.5px',
      },
      '&:hover fieldset': {
        borderColor: COLORS.primary, // Stay Navy Blue on hover
      },
      '&.Mui-focused fieldset': {
        borderColor: COLORS.primary, // Stay Navy Blue on focus
        borderWidth: '2.5px', // Only thickness changes
      },
    },
    '& .MuiInputBase-input': {
      fontFamily: FONT_FAMILY,
      color: COLORS.text,
    },
    '& .MuiInputLabel-root': {
      fontFamily: FONT_FAMILY,
      color: COLORS.primary, // Label always Navy Blue
      fontWeight: 500,
      '&.Mui-focused': {
        color: COLORS.primary
      }
    }
  }
};

/**
 * Step 1: Company Information
 */
const CompanyInfoStep = ({ data, onChange, onNext, loading }) => {
  const [errors, setErrors] = useState({});
  const [gstStates, setGstStates] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get(API_CONFIG.ENDPOINTS.STATES);
        if (!cancelled && Array.isArray(res.data)) {
          setGstStates(res.data);
        }
      } catch (e) {
        console.error('Failed to load GST states', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!data.companyName?.trim()) newErrors.companyName = 'Company name is required';
    if (!data.addressLine1?.trim()) newErrors.addressLine1 = 'Address Line 1 is required';
    if (!data.addressLine2?.trim()) newErrors.addressLine2 = 'Address Line 2 is required';
    if (!data.stateCode?.trim()) newErrors.stateCode = 'State (GST) is required';
    if (!data.companyPhone?.trim()) newErrors.companyPhone = 'Mobile is required';
    else if (!/^\d{1,15}$/.test(data.companyPhone.trim())) newErrors.companyPhone = 'Invalid phone number';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) onNext();
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 15);
    onChange('companyPhone', value);
  };

  return (
    <Box>
      <Typography variant="h4" sx={commonStyles.title}>
        Company Profile
      </Typography>
      <Typography variant="body1" sx={commonStyles.subtitle}>
        Setup your organization details
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Company Name"
            value={data.companyName || ''}
            onChange={(e) => onChange('companyName', e.target.value)}
            error={!!errors.companyName}
            helperText={errors.companyName}
            required
            sx={commonStyles.textField}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address Line 1"
            value={data.addressLine1 || ''}
            onChange={(e) => onChange('addressLine1', e.target.value)}
            error={!!errors.addressLine1}
            helperText={errors.addressLine1}
            required
            sx={commonStyles.textField}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address Line 2"
            value={data.addressLine2 || ''}
            onChange={(e) => onChange('addressLine2', e.target.value)}
            error={!!errors.addressLine2}
            helperText={errors.addressLine2}
            required
            sx={commonStyles.textField}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth variant="outlined" error={!!errors.stateCode} sx={commonStyles.textField} required>
            <InputLabel id="reg-company-state-label">State (GST)</InputLabel>
            <Select
              labelId="reg-company-state-label"
              label="State (GST)"
              value={data.stateCode ?? ''}
              onChange={(e) => onChange('stateCode', e.target.value)}
            >
              <MenuItem value="">
                <em>Select state</em>
              </MenuItem>
              {gstStates.map((s) => (
                <MenuItem key={s.stateCode} value={s.stateCode}>
                  {s.name} ({s.stateCode})
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {errors.stateCode || 'Place of supply for your business (intra vs inter-state billing)'}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Mobile"
            value={data.companyPhone || ''}
            onChange={handlePhoneChange}
            error={!!errors.companyPhone}
            helperText={errors.companyPhone || 'Numeric, max 15 digits'}
            required
            sx={commonStyles.textField}
            placeholder="e.g. 9876543210"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Tax Number (Optional)"
            value={data.taxNumber || ''}
            onChange={(e) => onChange('taxNumber', e.target.value)}
            sx={commonStyles.textField}
          />
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="flex-end" mt={5}>
        <Button
          variant="contained"
          size="large"
          onClick={handleNext}
          disabled={loading}
          sx={commonStyles.button}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

/**
 * Step 2: Admin Account
 */
const AdminAccountStep = ({ data, onChange, onNext, onBack, loading }) => {
  const [errors, setErrors] = useState({});
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(null);

  const checkEmail = async (email) => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return true;

    setEmailChecking(true);
    setErrors(prev => {
      const newErrs = { ...prev };
      delete newErrs.adminEmail;
      return newErrs;
    });

    try {
      const isAvailable = await tenantService.checkEmailAvailability(email);
      setEmailAvailable(isAvailable);
      if (!isAvailable) {
        setErrors(prev => ({ ...prev, adminEmail: 'This email is already registered.' }));
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error checking email:', error);
      return true;
    } finally {
      setEmailChecking(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!data.adminName?.trim()) newErrors.adminName = 'Admin name is required';
    if (!data.adminEmail?.trim()) newErrors.adminEmail = 'Admin email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.adminEmail)) newErrors.adminEmail = 'Invalid email address';
    else if (emailAvailable === false) newErrors.adminEmail = 'This email is already registered';

    if (!data.adminPassword?.trim()) newErrors.adminPassword = 'Password is required';
    else if (data.adminPassword.length < 8) newErrors.adminPassword = 'Password must be at least 8 characters';

    if (data.adminPassword !== data.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (data.adminEmail && !errors.adminEmail && emailAvailable === null && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.adminEmail)) {
      const isOk = await checkEmail(data.adminEmail);
      if (!isOk) return;
    }
    if (validateForm()) onNext();
  };

  return (
    <Box>
      <Typography variant="h4" sx={commonStyles.title}>
        Admin Credentials
      </Typography>
      <Typography variant="body1" sx={commonStyles.subtitle}>
        Set up primary administrator access
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Full Name"
            value={data.adminName || ''}
            onChange={(e) => onChange('adminName', e.target.value)}
            error={!!errors.adminName}
            helperText={errors.adminName}
            required
            sx={commonStyles.textField}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={data.adminEmail || ''}
            onChange={(e) => {
              onChange('adminEmail', e.target.value);
              setEmailAvailable(null);
            }}
            onBlur={(e) => checkEmail(e.target.value)}
            error={!!errors.adminEmail}
            helperText={errors.adminEmail || (emailChecking ? 'Checking availability...' : '')}
            required
            sx={commonStyles.textField}
            InputProps={{
              endAdornment: emailChecking ? <CircularProgress size={20} /> : null
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={data.adminPassword || ''}
            onChange={(e) => onChange('adminPassword', e.target.value)}
            error={!!errors.adminPassword}
            helperText={errors.adminPassword || 'Min 8 characters'}
            required
            sx={commonStyles.textField}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Confirm Password"
            type="password"
            value={data.confirmPassword || ''}
            onChange={(e) => onChange('confirmPassword', e.target.value)}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            required
            sx={commonStyles.textField}
          />
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="space-between" mt={5}>
        <Button onClick={onBack} sx={commonStyles.backButton}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={loading || emailChecking}
          sx={commonStyles.button}
        >
          {emailChecking ? 'Checking...' : 'Next'}
        </Button>
      </Box>
    </Box>
  );
};

/**
 * Step 3: Plan Selection (Premium Cards)
 */
const PlanSelectionStep = ({ data, onChange, onNext, onBack, loading }) => {
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const plansData = await subscriptionService.getSubscriptionPlans();
      const activePlans = Array.isArray(plansData)
        ? plansData.filter(p => String(p.status || 'ACTIVE').toUpperCase() === 'ACTIVE')
        : [];
      setPlans(activePlans);
    } catch (error) {
      console.error('Error loading plans:', error);
      toast.error('Failed to load plans');
    } finally {
      setPlansLoading(false);
    }
  };

  const handlePlanSelect = (plan) => {
    onChange('planCode', plan.planCode);
    onChange('selectedPlan', plan);
  };

  const buildPlanFeatures = (plan) => {
    const features = plan.features;
    const hasAnyFlags =
      features &&
      Object.keys(features).some((k) => PLAN_FEATURE_FLAGS.some((f) => f.id === k));

    const treatAllEnabled = !features || Object.keys(features).length === 0 || !hasAnyFlags;

    return PLAN_FEATURE_FLAGS.map((f) => ({
      label: f.label,
      enabled: treatAllEnabled ? true : !!(features && features[f.id] === true),
    }));
  };

  return (
    <Box>
      <Typography variant="h4" sx={commonStyles.title}>
        Choose Your Plan
      </Typography>
      <Typography variant="body1" sx={commonStyles.subtitle}>
        Scalable pricing for your business needs
      </Typography>

      <Box display="flex" justifyContent="center" mb={4}>
        <Paper elevation={0} sx={{ p: 0.5, backgroundColor: '#e3f2fd', borderRadius: 2, display: 'flex' }}>
          <Button
            variant={billingCycle === 'monthly' ? 'contained' : 'text'}
            onClick={() => setBillingCycle('monthly')}
            sx={{
              borderRadius: 1.5,
              fontFamily: FONT_FAMILY,
              textTransform: 'none',
              px: 3,
              backgroundColor: billingCycle === 'monthly' ? COLORS.primary : 'transparent',
              color: billingCycle === 'monthly' ? '#fff' : COLORS.text
            }}
          >
            Monthly
          </Button>
          <Button
            variant={billingCycle === 'yearly' ? 'contained' : 'text'}
            onClick={() => setBillingCycle('yearly')}
            sx={{
              borderRadius: 1.5,
              fontFamily: FONT_FAMILY,
              textTransform: 'none',
              px: 3,
              backgroundColor: billingCycle === 'yearly' ? COLORS.primary : 'transparent',
              color: billingCycle === 'yearly' ? '#fff' : COLORS.text,
              ml: 1
            }}
          >
            Yearly (Save 20%)
          </Button>
        </Paper>
      </Box>

      {plansLoading ? (
        <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={3} justifyContent="center" alignItems="stretch">
          {plans.map((plan) => {
            const isSelected = data.planCode === plan.planCode;
            const isPopular = plan.isPopular;

            // Fix for undefined price: Check map structure against API
            // Usually API returns: priceMonthly, priceYearly
            const price = billingCycle === 'monthly' ? plan.priceMonthly : plan.priceYearly;

            return (
              <Grid item xs={12} md={4} key={plan.id} sx={{ display: 'flex' }}>
                <Card
                  elevation={isSelected || isPopular ? 10 : 2}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    border: isSelected ? `2px solid ${COLORS.accent}` : '1px solid transparent',
                    transform: isSelected ? 'scale(1.02)' : 'none',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    borderRadius: 2,
                    backgroundColor: isSelected ? '#f5f9ff' : '#fff'
                  }}
                  onClick={() => handlePlanSelect(plan)}
                >
                  {(isPopular) && (
                    <Chip
                      label="MOST POPULAR"
                      color="secondary"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 15,
                        right: 15,
                        fontWeight: 'bold',
                        fontFamily: FONT_FAMILY,
                        backgroundColor: COLORS.accent,
                        color: '#fff'
                      }}
                    />
                  )}
                  <CardContent sx={{ p: 4, flexGrow: 1, textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, mb: 1, color: COLORS.secondary }}>
                      {plan.name}
                    </Typography>
                    <Typography variant="h3" sx={{ fontFamily: FONT_FAMILY, fontWeight: 800, mb: 2, color: COLORS.primary }}>
                      {formatPrice(price, plan.currency)}
                      <Typography component="span" variant="body1" color="textSecondary" sx={{ fontFamily: FONT_FAMILY }}>
                        /{billingCycle === 'monthly' ? 'mo' : 'yr'}
                      </Typography>
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ textAlign: 'left', pl: 1 }}>
                      {buildPlanFeatures(plan).map((feature, i) => (
                        <Box key={i} display="flex" alignItems="center" mb={1.5}>
                          {feature.enabled ? (
                            <CheckMarkIcon sx={{ fontSize: 20, mr: 1.5, color: COLORS.success }} />
                          ) : (
                            <CloseIcon sx={{ fontSize: 20, mr: 1.5, color: COLORS.muted }} />
                          )}
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: FONT_FAMILY,
                              color: feature.enabled ? COLORS.text : COLORS.muted,
                            }}
                          >
                            {feature.label}
                          </Typography>
                        </Box>
                      ))}
                      <Box display="flex" alignItems="center" mb={1.5}>
                        <CheckMarkIcon sx={{ fontSize: 20, mr: 1.5, color: COLORS.success }} />
                        <Typography variant="body2" sx={{ fontFamily: FONT_FAMILY, color: COLORS.text, fontWeight: 600 }}>
                          {plan.trialDays}-Day Free Trial
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                  <Box p={3} pt={0}>
                    <Button
                      fullWidth
                      variant={isSelected ? "contained" : "outlined"}
                      sx={{
                        fontFamily: FONT_FAMILY,
                        borderRadius: 1,
                        py: 1.2,
                        borderColor: COLORS.primary,
                        color: isSelected ? '#fff' : COLORS.primary,
                        backgroundColor: isSelected ? COLORS.primary : 'transparent',
                        '&:hover': {
                          backgroundColor: isSelected ? COLORS.accent : '#e3f2fd'
                        }
                      }}
                    >
                      {isSelected ? 'Selected' : 'Select Plan'}
                    </Button>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Box display="flex" justifyContent="space-between" mt={5}>
        <Button onClick={onBack} sx={commonStyles.backButton}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={() => data.planCode && onNext()}
          disabled={loading || !data.planCode}
          sx={commonStyles.button}
        >
          Review & Join
        </Button>
      </Box>
    </Box>
  );
};

/**
 * Step 4: Complete Registration
 */
const CompleteStep = ({ data, onComplete, loading, error }) => {
  return (
    <Box>
      <Typography variant="h4" sx={commonStyles.title}>
        Final Verification
      </Typography>
      <Typography variant="body1" sx={commonStyles.subtitle}>
        Please confirm your details below
      </Typography>

      <Card variant="outlined" sx={{ mb: 4, borderRadius: 2, borderColor: COLORS.border, backgroundColor: '#f9fafb' }}>
        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box mb={3}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ fontFamily: FONT_FAMILY, letterSpacing: 1, mb: 0.5 }}>
                  ORGANIZATION
                </Typography>
                <Typography variant="h6" sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: COLORS.text }}>
                  {data.companyName}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: FONT_FAMILY, color: COLORS.muted }}>
                  {data.companyPhone}
                </Typography>
                {(data.addressLine1 || data.addressLine2) && (
                  <Typography variant="body2" sx={{ fontFamily: FONT_FAMILY, color: COLORS.muted, mt: 1 }}>
                    {[data.addressLine1, data.addressLine2].filter(Boolean).join(', ')}
                  </Typography>
                )}
                {data.stateCode ? (
                  <Typography variant="body2" sx={{ fontFamily: FONT_FAMILY, color: COLORS.muted, mt: 0.5 }}>
                    State (GST): {data.stateCode}
                  </Typography>
                ) : null}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box mb={3}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ fontFamily: FONT_FAMILY, letterSpacing: 1, mb: 0.5 }}>
                  ADMINISTRATOR
                </Typography>
                <Typography variant="h6" sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: COLORS.text }}>
                  {data.adminName}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: FONT_FAMILY, color: COLORS.muted }}>
                  {data.adminEmail}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 1, borderColor: COLORS.border }} />

          <Box mt={3} display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="subtitle2" color="textSecondary" sx={{ fontFamily: FONT_FAMILY, letterSpacing: 1 }}>
                SELECTED PLAN
              </Typography>
              <Typography variant="h5" sx={{ fontFamily: FONT_FAMILY, fontWeight: 800, color: COLORS.accent }}>
                {data.selectedPlan?.name}
              </Typography>
            </Box>
            <Chip
              label={`${data.selectedPlan?.trialDays || 14} DAYS FREE TRIAL`}
              sx={{
                fontFamily: FONT_FAMILY,
                fontWeight: 700,
                backgroundColor: '#e8f5e9',
                color: COLORS.success,
                borderRadius: 1
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Box textAlign="center">
        <FormControlLabel
          control={
            <Checkbox
              checked={data.acceptTerms || false}
              onChange={(e) => data.onChange('acceptTerms', e.target.checked)}
              sx={{ color: COLORS.primary, '&.Mui-checked': { color: COLORS.primary } }}
            />
          }
          label={
            <Typography variant="body1" sx={{ fontFamily: FONT_FAMILY, color: COLORS.text }}>
              I agree to the {' '}
              <Link href="/terms" target="_blank" underline="hover" color="primary">Terms of Service</Link>
              {' '} and {' '}
              <Link href="/privacy" target="_blank" underline="hover" color="primary">Privacy Policy</Link>
            </Typography>
          }
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box mt={4}>
          <Button
            variant="contained"
            size="large"
            onClick={onComplete}
            disabled={loading || !data.acceptTerms}
            sx={{ ...commonStyles.button, px: 8, py: 1.5, fontSize: '1.2rem', boxShadow: '0 4px 12px rgba(26, 35, 126, 0.4)' }}
            startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <CheckIcon />}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Main Page Component
 */
const TenantRegistrationPage = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleDataChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => setActiveStep(prev => prev + 1);
  const handleBack = () => setActiveStep(prev => prev - 1);

  const handleComplete = async () => {
    try {
      setLoading(true);
      setError(null);
      const registrationData = {
        companyName: formData.companyName,
        adminName: formData.adminName,
        adminEmail: formData.adminEmail,
        adminPassword: formData.adminPassword,
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        stateCode: formData.stateCode,
        companyPhone: formData.companyPhone,
        taxNumber: formData.taxNumber,
        planCode: formData.planCode,
        startTrial: true
      };

      const response = await tenantService.registerTenant(registrationData);
      if (response.accessToken) storeUserData(response);

      // Company registration succeeded — fire Meta Pixel CompleteRegistration event
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('track', 'CompleteRegistration', {
          content_name: 'Company Signup',
          status: true,
        });
      }

      const trialDays = formData.selectedPlan?.trialDays || 14;
      const trialEndDate = new Date();
      trialEndDate.setDate(trialEndDate.getDate() + trialDays);
      const formattedDate = trialEndDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

      toast.success('Registration successful! Redirecting...', { duration: 4000 });
      navigate('/dashboard');
    } catch (err) {
      console.error('Registration error:', err);
      const msg = err.response?.data?.message || err.message || 'Registration failed.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    const commonProps = {
      data: { ...formData, onChange: handleDataChange },
      onChange: handleDataChange,
      loading
    };

    let content = null;
    switch (activeStep) {
      case 0: content = <CompanyInfoStep {...commonProps} onNext={handleNext} />; break;
      case 1: content = <AdminAccountStep {...commonProps} onNext={handleNext} onBack={handleBack} />; break;
      case 2: content = <PlanSelectionStep {...commonProps} onNext={handleNext} onBack={handleBack} />; break;
      case 3: content = <CompleteStep data={{ ...formData, onChange: handleDataChange }} onComplete={handleComplete} loading={loading} error={error} />; break;
      default: content = null;
    }

    return (
      <Fade in={true} key={activeStep} timeout={600}>
        <Box>{content}</Box>
      </Fade>
    );
  };

  return (
    <Box sx={commonStyles.root}>
      {/* Navigation Header */}
      <Container maxWidth="lg" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{
            fontFamily: FONT_FAMILY,
            color: COLORS.primary,
            fontWeight: 700,
            fontSize: '1rem',
            '&:hover': { backgroundColor: 'transparent', opacity: 0.8 }
          }}
        >
          Back to Login
        </Button>
      </Container>

      <Paper elevation={3} sx={commonStyles.paper}>
        {/* Stepper with explicit font styling */}
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 6 }}>
          {steps.map((step) => (
            <Step key={step.label}>
              <StepLabel
                StepIconProps={{
                  sx: {
                    fontSize: 32,
                    '&.Mui-active': { color: COLORS.accent },
                    '&.Mui-completed': { color: COLORS.success },
                  }
                }}
                sx={{
                  '& .MuiStepLabel-label': {
                    fontFamily: FONT_FAMILY,
                    fontSize: '1rem',
                    fontWeight: 600,
                    mt: 1,
                    color: COLORS.text,
                    '&.Mui-active': { color: COLORS.primary }
                  }
                }}
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Divider sx={{ mb: 5, borderColor: '#eceff1' }} />

        {renderStepContent()}
      </Paper>
    </Box>
  );
};

export default TenantRegistrationPage;
